import { useEffect, useRef, useState } from 'react';
import { GetServerSidePropsContext, PreviewData } from 'next';
import Head from 'next/head';
import { ParsedUrlQuery } from 'querystring';

import { GlobalHeader, type GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { getGlobalHeaderServerSideProps } from '@eventbrite/consumer-header.global-header/server';
import { getGlobalFooterServerSideProps } from '@eventbrite/legacy-infra.site-footer/server';
import { EventbriteHeader, type Stage } from '@eventbrite/webapp-constants';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import {
  fetchOrganizerPageDataWithDiagnostics,
  OrganizerGoneError,
  type OrganizerPageData,
} from '@/api/services/organizer-profile-service';
import { getFinalLocale } from '@/lib/localeInfo';
import { Footer, type FooterProps, TagManager } from '@/components';
import { ErrorPageContent } from '@/components/ErrorPages';
import { errorMessages } from '@/components/ErrorPages/errorMessages';
import OrganizerPage from '@/components/OrganizerPage';
import type { AuthState } from '@/components/OrganizerPage/_shared/EventsBucket/EventsBucket';
import { useEditorContextRefresh } from '@/hooks/useEditorContextRefresh';
import { useOrganizerUserData } from '@/hooks/useOrganizerUserData';
import type { OrganizerData } from '@/types/organizer';
import { createDefaultLocaleInfo, getDefaultDomainUrl, getDomainUrl } from '@/utils';
import { ssrMetrics } from '@/utils/pysoa';
import { getLocaleMessages } from '../../i18n/locale';
import { CACHE_CONTROL_TTL, CACHE_CONTROL_NO_CACHE, NO_CACHE_QUERY_PARAM } from '@/constants/cache';
import { evaluateFeatureFlags, DEFAULT_FEATURE_FLAGS } from '@/utils/featureFlags';
import type { FeatureFlags } from '@/utils/featureFlags';
import { FeatureFlagsProvider } from '@/contexts/FeatureFlagsContext';

const ORGANIZER_ID_REGEX = /^([a-z0-9-]+-)?(\d+)\/?$/;
const MAX_OG_DESCRIPTION_LENGTH = 300;

function stripHtmlToPlainText(html: string): string {
  const text = html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= MAX_OG_DESCRIPTION_LENGTH) return text;
  return `${text.slice(0, MAX_OG_DESCRIPTION_LENGTH - 1)}…`;
}

function absoluteUrlForOpenGraph(siteBaseUrl: string, url: string): string | undefined {
  const u = url.trim();
  if (!u) return undefined;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('//')) return `https:${u}`;
  const path = u.startsWith('/') ? u : `/${u}`;
  return `${siteBaseUrl.replace(/\/$/, '')}${path}`;
}

function organizerOpenGraphDescription(organizer: OrganizerData): string {
  const fromBio = stripHtmlToPlainText(organizer.bio);
  if (fromBio) return fromBio;
  const loc = organizer.locations
    .map((l) => l.description.trim())
    .filter(Boolean)
    .join(', ');
  if (loc) return `${organizer.name} — ${loc}`;
  return organizer.name;
}

function organizerOpenGraphImageUrl(
  organizer: OrganizerData,
  siteBaseUrl: string,
): string | undefined {
  const raw = organizer.coverUrl?.trim() || organizer.avatarUrl.trim();
  return absoluteUrlForOpenGraph(siteBaseUrl, raw);
}

function buildHeaderFooterProps(
  context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>,
  globalHeaderResult: PromiseSettledResult<GlobalHeaderProps>,
  globalFooterResult: PromiseSettledResult<
    Awaited<ReturnType<typeof getGlobalFooterServerSideProps>>
  >,
  findEventsLabel: string,
) {
  const serverUrl = getDomainUrl(context.req) || getDefaultDomainUrl();
  const localeInfo = createDefaultLocaleInfo(context.req);

  const emptyGlobalHeader: GlobalHeaderProps = {
    env: {
      serverUrl: serverUrl,
    },
    request: {
      path: context.req.url,
      session_id: context.req.headers[EventbriteHeader.EB_SESSION_ID] as string,
    },
    isHomePage: false,
  };

  const globalHeaderProps =
    globalHeaderResult.status === 'fulfilled' ? globalHeaderResult.value : emptyGlobalHeader;

  const globalFooterProps =
    globalFooterResult.status === 'fulfilled'
      ? {
          ...globalFooterResult.value,
          thirdFooterColumn: globalFooterResult.value.thirdFooterColumn ?? [],
          eventsToPlan: globalFooterResult.value.eventsToPlan ?? [],
          popularSearches: globalFooterResult.value.popularSearches ?? [],
        }
      : {
          thirdFooterColumnName: findEventsLabel,
          thirdFooterColumn: [],
          popularSearches: [],
          eventsToPlan: [],
          showMinimalLinks: false,
          impressumHelpLink: '',
        };

  const footerProps = {
    serverUrl,
    localeInfo,
    isUserAuthenticated: false,
    globalFooterProps,
  };

  return { globalHeaderProps, footerProps, serverUrl };
}

export async function getServerSideProps(
  context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>,
) {
  const rawOrganizerId = context.params?.organizerId as string;
  const match = rawOrganizerId?.match(ORGANIZER_ID_REGEX);

  return ssrMetrics.measure(async () => {
    const sourceLocale = getFinalLocale(getLocaleFromRequest(context.req));
    const localeMessages = await getLocaleMessages(sourceLocale);
    const findEventsLabel = localeMessages['Find Events'] ?? 'Find Events';

    const fetches: [
      Promise<GlobalHeaderProps>,
      Promise<Awaited<ReturnType<typeof getGlobalFooterServerSideProps>>>,
      ...Promise<{ pageData: OrganizerPageData; hasRecoverableFetchFailure: boolean }>[],
    ] = [
      ssrMetrics.measure(
        () => getGlobalHeaderServerSideProps(context),
        'getServerSideProps.globalHeader',
      ),
      ssrMetrics.measure(
        () => getGlobalFooterServerSideProps(context),
        'getServerSideProps.globalFooter',
      ),
    ];

    if (match) {
      fetches.push(
        ssrMetrics.measure(
          () => fetchOrganizerPageDataWithDiagnostics(match[2], context.req),
          'getServerSideProps.fetchOrganizerPageData',
        ),
      );
    }

    const featureFlagsFetch = match
      ? ssrMetrics.measure(
          () => evaluateFeatureFlags({ organizerId: match[2] }),
          'getServerSideProps.featureFlags',
        )
      : Promise.resolve(DEFAULT_FEATURE_FLAGS);

    const [results, featureFlagsResult] = await Promise.all([
      Promise.allSettled(fetches),
      featureFlagsFetch.then(
        (value) => ({ status: 'fulfilled' as const, value }),
        () => ({ status: 'rejected' as const, value: DEFAULT_FEATURE_FLAGS }),
      ),
    ]);
    const globalHeaderResult = results[0] as PromiseSettledResult<GlobalHeaderProps>;
    const globalFooterResult = results[1] as PromiseSettledResult<
      Awaited<ReturnType<typeof getGlobalFooterServerSideProps>>
    >;

    const { globalHeaderProps, footerProps, serverUrl } = buildHeaderFooterProps(
      context,
      globalHeaderResult,
      globalFooterResult,
      findEventsLabel,
    );

    if (!match) {
      context.res.statusCode = 404;
      return {
        props: { isNotFound: true as const, globalHeaderProps, footerProps, serverUrl },
      };
    }

    const organizerPageResult = results[2] as PromiseSettledResult<{
      pageData: OrganizerPageData;
      hasRecoverableFetchFailure: boolean;
    }>;

    if (organizerPageResult.status === 'rejected') {
      context.res.statusCode = organizerPageResult.reason instanceof OrganizerGoneError ? 410 : 404;
      return {
        props: { isNotFound: true as const, globalHeaderProps, footerProps, serverUrl },
      };
    }

    const { pageData, hasRecoverableFetchFailure } = organizerPageResult.value;
    const featureFlags = featureFlagsResult.value;

    const cacheControl =
      NO_CACHE_QUERY_PARAM in context.query || hasRecoverableFetchFailure
        ? CACHE_CONTROL_NO_CACHE
        : CACHE_CONTROL_TTL;

    context.res.setHeader('Cache-Control', cacheControl);

    return {
      props: {
        globalHeaderProps,
        footerProps,
        serverUrl,
        featureFlags,
        ...pageData,
      },
    };
  }, 'getServerSideProps');
}

export type OrganizerProfilePageProps = OrganizerPageData;

interface NotFoundProps {
  isNotFound: true;
  serverUrl: string;
  stage: Stage;
  globalHeaderProps: GlobalHeaderProps;
  footerProps: FooterProps;
  featureFlags: FeatureFlags;
}

interface NormalProps extends OrganizerProfilePageProps {
  isNotFound?: false;
  serverUrl: string;
  stage: Stage;
  globalHeaderProps: GlobalHeaderProps;
  footerProps: FooterProps;
  featureFlags: FeatureFlags;
}

type Props = NotFoundProps | NormalProps;

export function OrganizerProfile(props: Props) {
  const { globalHeaderProps, footerProps } = props;

  if (props.isNotFound) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Head>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <GlobalHeader {...globalHeaderProps} disableFetchUser={false} />
        <main style={{ flex: 1 }}>
          <ErrorPageContent
            error={{
              message: errorMessages.notFoundError.message.id,
              description: errorMessages.notFoundError.description.id,
            }}
          />
        </main>
        <Footer {...footerProps} />
      </div>
    );
  }

  return <OrganizerProfileContent {...props} />;
}

function OrganizerProfileContent({
  globalHeaderProps,
  footerProps,
  serverUrl,
  stage,
  featureFlags,
  organizer: ssrOrganizer,
  upcomingEvents: ssrUpcomingEvents,
  hasMoreUpcoming: ssrHasMoreUpcoming,
  upcomingEventsTotal: ssrUpcomingEventsTotal,
  collections: ssrCollections,
  collectionsHasMore: ssrCollectionsHasMore,
  collectionsTotal: ssrCollectionsTotal,
}: NormalProps) {
  const [pageData, setPageData] = useState<OrganizerPageData>({
    organizer: ssrOrganizer,
    upcomingEvents: ssrUpcomingEvents,
    hasMoreUpcoming: ssrHasMoreUpcoming,
    upcomingEventsTotal: ssrUpcomingEventsTotal,
    collections: ssrCollections,
    collectionsHasMore: ssrCollectionsHasMore,
    collectionsTotal: ssrCollectionsTotal,
  });

  const { data: userData } = useOrganizerUserData(pageData.organizer.id);
  const canManageProfile = !!pageData.organizer.id && !!userData?.canManageProfile;
  const authState: AuthState =
    userData === undefined ? 'unknown' : userData.userId ? 'authenticated' : 'anonymous';
  const { data: editorContextData, isFetching: isRefreshingEditorContext } =
    useEditorContextRefresh({
      organizerId: pageData.organizer.id,
      enabled: canManageProfile,
    });

  useEffect(() => {
    if (editorContextData?.organizer) {
      setPageData(editorContextData);
    }
  }, [editorContextData]);

  const hasStartedRefresh = useRef(false);
  if (canManageProfile && isRefreshingEditorContext) {
    hasStartedRefresh.current = true;
  }

  let refreshPhase: 'pending' | 'refreshing' | 'done' = 'pending';
  if (hasStartedRefresh.current) {
    refreshPhase = isRefreshingEditorContext ? 'refreshing' : 'done';
  }

  const ogDescription = organizerOpenGraphDescription(pageData.organizer);
  const ogImage = organizerOpenGraphImageUrl(pageData.organizer, serverUrl);

  return (
    <FeatureFlagsProvider flags={featureFlags}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Head>
          <title>{pageData.organizer.name}</title>
          <meta property="og:title" content={pageData.organizer.name} />
          <meta property="og:description" content={ogDescription} />
          {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        </Head>
        <GlobalHeader {...globalHeaderProps} disableFetchUser={false} />
        <main style={{ flex: 1 }}>
          <OrganizerPage
            serverUrl={serverUrl}
            organizer={pageData.organizer}
            isOwner={canManageProfile}
            authState={authState}
            refreshPhase={refreshPhase}
            upcomingEvents={pageData.upcomingEvents}
            hasMoreUpcoming={pageData.hasMoreUpcoming}
            upcomingEventsTotal={pageData.upcomingEventsTotal}
            collections={pageData.collections}
            collectionsHasMore={pageData.collectionsHasMore}
            collectionsTotal={pageData.collectionsTotal}
            locale={footerProps.localeInfo.locale}
          />
        </main>
        <Footer {...footerProps} />
        <TagManager publicUserId="" stage={stage} />
      </div>
    </FeatureFlagsProvider>
  );
}

export default OrganizerProfile;
